package com.downdogapp.client.views

import com.downdogapp.*
import com.downdogapp.Border.NONE
import com.downdogapp.BoxSizing.BORDER_BOX
import com.downdogapp.Cursor.POINTER
import com.downdogapp.Display.FLEX
import com.downdogapp.FlexDirection.ROW
import com.downdogapp.FontWeight.BOLD
import com.downdogapp.FontWeight.REGULAR
import com.downdogapp.FontWeight.SEMIBOLD
import com.downdogapp.JustifyContent.SPACE_BETWEEN
import com.downdogapp.Position.ABSOLUTE
import com.downdogapp.Position.RELATIVE
import com.downdogapp.Size.AUTO
import com.downdogapp.Size.Companion.percent
import com.downdogapp.Size.Companion.px
import com.downdogapp.Size.Companion.vh
import com.downdogapp.client.*
import com.downdogapp.client.api.AppType
import com.downdogapp.client.api.ProductPeriod.MONTHLY
import com.downdogapp.client.api.ProductPeriod.YEARLY
import com.downdogapp.client.controllers.PurchaseViewController
import com.downdogapp.client.resources.Images
import com.downdogapp.client.resources.image
import com.downdogapp.client.singleton.Browser
import com.downdogapp.client.singleton.PayPal
import com.downdogapp.client.widget.*
import com.downdogapp.client.widget.ScreenWidth.PHONE
import kotlinx.browser.window
import org.w3c.dom.*
import kotlin.js.Promise

actual class PurchaseView actual constructor(
  private val controller: PurchaseViewController
) : TopLevelView(), View {

  private var paymentOptionViewContainer: HTMLDivElement
  private var closeButtonContainer: HTMLDivElement
  private var footerContainer: HTMLDivElement
  private var leftToggle: ProductToggle
  private var rightToggle: ProductToggle

  private var alternateCurrencySelected = false

  private val appTileImageViews =
    mutableMapOf<AppType, HTMLImageElement>()

  override fun onResize() {
    refreshView()
  }

  override fun onBecameVisible() {
    refreshView()

    window
      .asDynamic()
      .paypal
      .Buttons(
        jsObj(
          "style" to jsObj(
            "tagline" to "false",
            "label" to "pay",
            "size" to "responsive",
            "shape" to "rect",
            "color" to "white"
          ),
          "fundingSource" to window.asDynamic().paypal.FUNDING.PAYPAL,
          "createSubscription" to js(
            """
              function(data, actions) {
                return client.com.downdogapp.client.views.createPayPalSubscription(data, actions);
              }
            """
          ),
          "onApprove" to js(
            """
              function(data, actions) {
                return client.com.downdogapp.client.views.createPayPalMembership(data, actions);
              }
            """
          )
        )
      )
      .render("#paypal-button-container")
  }

  override var root: HTMLElement =
    controllerView {
      bg = background(image = Images.MEMBERSHIP)

      fg = foreground {
        centerHorizontally {
          div {
            screenWidthDependent { screendWidth ->
              width = px(if (screendWidth == PHONE) 330 else 500)
            }

            div {
              textAlign = TextAlign.CENTER
              marginTop = px(64)
              marginHorizontal = AUTO
              label(fontSize = 18) {
                innerHTML = controller.config.subtitle.replace("\n", "<br>")
                screenWidthDependent { screenWidth ->
                  fontSize = px(if (screenWidth == PHONE) 14 else 18)
                }
              }
            }

            div {
              textAlign = TextAlign.CENTER
              screenWidthDependent { screenWidth ->
                marginTop = px(if (screenWidth == PHONE) 10 else 30)
              }
              marginHorizontal = AUTO
              label(
                text = controller.config.title,
                fontSize = 24,
                fontWeight = FontWeight.MEDIUM
              ) {
                screenWidthDependent { screenWidth ->
                  fontSize = px(if (screenWidth == PHONE) 18 else 24)
                }
              }
            }

            div {
              screenWidthDependent { screenWidth ->
                marginTop = px(if (screenWidth == PHONE) 6 else 20)
                width = px(if (screenWidth == PHONE) 200 else 280)
              }

              marginHorizontal = AUTO
              display = FLEX
              justifyContent = SPACE_BETWEEN

              for (appType in controller.config.singleAppTiles) {
                controller.getImage(appType)?.let {
                  div {
                    val image =
                      img {
                        screenWidthDependent { screenWidth ->
                          width = px(if (screenWidth == PHONE) 34 else 44)
                          height = px(if (screenWidth == PHONE) 34 else 44)
                        }
                        image = it
                      }

                    appTileImageViews[appType] = image
                  }
                }
              }
            }

            paymentOptionViewContainer = div {
              screenWidthDependent { screenWidth ->
                marginTop = px(if (screenWidth == PHONE) 24 else 75)
              }
              marginHorizontal = px(20)
              flexDirection = ROW
              display = FLEX
              justifyContent = SPACE_BETWEEN

              val leftSelectedAsDefault = productPeriod == YEARLY
              leftToggle = productToggle(left = true).apply {
                setBackgroundSelected(leftSelectedAsDefault)
              }
              rightToggle = productToggle(left = false).apply {
                setBackgroundSelected(!leftSelectedAsDefault)
              }
            }

            controller.alternateCurrencyCode?.let { alt ->
              centerHorizontally {
                marginTop = px(25)
                togglePill(
                  lazyLeftText = { controller.config.defaultCurrencyCode!! },
                  lazyRightText = { alt },
                  getValue = { alternateCurrencySelected },
                  onValueChanged = { right ->
                    alternateCurrencySelected = right
                    currencyCode = if (right) alt else controller.config.defaultCurrencyCode
                    refreshView()
                  }
                )
              }
            }

            centerHorizontally {
              marginTop = px(3)

              div {
                width = percent(100)
                div {
                  paddingLeft = HORIZONTAL_CONTENT_GUTTER_PX
                  paddingRight = HORIZONTAL_CONTENT_GUTTER_PX

                  button {
                    screenWidthDependent { screenWidth ->
                      height = if (screenWidth == PHONE) px(50) else ROUNDED_TEXT_BUTTON_HEIGHT_PX
                      fontSize = px(if (screenWidth == PHONE) 15 else 19)
                      marginTop = px(if (screenWidth == PHONE) 10 else 35)
                    }
                    textContent = controller.config.purchaseButtonText.uppercase()
                    width = percent(100)

                    borderRadius = ROUNDED_TEXT_BUTTON_HEIGHT_PX / 2
                    border = NONE

                    fontFamily = FONT_FAMILY
                    fontWeight = BOLD

                    bgColor = Color.DOWN_DOG_BLUE
                    txtColor = Color.WHITE

                    onClick {
                      controller.payWithCreditCardClicked()
                    }
                  }
                }
              }

              div {
                marginTop = px(12)
                width = percent(100)
                height = ROUNDED_TEXT_BUTTON_HEIGHT_PX
                position = RELATIVE

                centerInParent {
                  div {
                    id = "paypal-button-container"
                    screenWidthDependent { screenWidth ->
                      width = px(if (screenWidth == PHONE) 260 else 320)
                    }
                  }
                }

                div {
                  position = ABSOLUTE
                  left = px(0)
                  top = px(0)
                  width = percent(100)
                  style.setProperty("pointer-events", "none")
                  zIndex = ZIndex.zVal(500)
                  paddingLeft = HORIZONTAL_CONTENT_GUTTER_PX
                  paddingRight = HORIZONTAL_CONTENT_GUTTER_PX
                  boxSizing = BORDER_BOX

                  button {
                    screenWidthDependent { screenWidth ->
                      height = if (screenWidth == PHONE) px(50) else ROUNDED_TEXT_BUTTON_HEIGHT_PX
                      fontSize = px(if (screenWidth == PHONE) 15 else 19)
                    }
                    textContent = Strings.PAY_WITH_PAYPAL.uppercase()
                    width = percent(100)

                    borderRadius = ROUNDED_TEXT_BUTTON_HEIGHT_PX / 2
                    border = NONE

                    cursor = POINTER

                    fontFamily = FONT_FAMILY
                    fontWeight = BOLD

                    bgColor = Color.WHITE
                    txtColor = Color.DOWN_DOG_BLUE
                  }
                }
              }

              controller.affordabilityText?.let { text ->
                div {
                  width = px(300)
                  borderRadius = px(4)
                  screenWidthDependent { screenWidth ->
                    marginVertical = px(if (screenWidth == PHONE) 0 else 10)
                    height = px(if (screenWidth == PHONE) 30 else 50)
                  }

                  centerInParent {
                    val affordabilityText = textButton(
                      text = text,
                      fontSize = 20,
                      clickFn = controller::affordabilityClicked
                    ) {
                      opacity = 0.8
                    }
                    screenWidthDependent { screenWidth ->
                      affordabilityText.fontSize = px(if (screenWidth == PHONE) 14 else 20)
                    }

                    onClick {
                      controller.affordabilityClicked()
                    }
                  }

                  onClick {
                    controller.affordabilityClicked()
                  }
                }
              }

              footerContainer = div {
                screenWidthDependent { screendWidth ->
                  width = px(if (screendWidth == PHONE) 300 else 450)
                }
                marginTop = px(25)
                textAlign = TextAlign.CENTER

                div {
                  val text =
                    (if (controller.alternateCurrencyCode != null)
                      ""
                    else
                      Strings.AMOUNTS_CURRENCY_PAYMENTS_SECURE_MESSAGE(currencyCode!!) + " ") +
                    Strings.RECEIPT_SENT_TO_EMAIL_MESSAGE(MANIFEST.email!!)
                  label(
                    text = text,
                    fontSize = 12
                  ) {
                    txtColor = Color.white(0.85)
                    screenWidthDependent { screenWidth ->
                      fontSize = px(if (screenWidth == PHONE) 12 else 14)
                    }
                  }
                }
              }
            }
          }
        }

        closeButtonContainer = div {
          position = ABSOLUTE
          top = vh(2)

          imageButton(image = Images.LEFT_ARROW_ICON, clickFn = controller::onBackClicked)
        }
      }
    }

  actual override fun refreshView() {
    closeButtonContainer.apply {
      left = if (Browser.windowWidth < px(500)) {
        px(10)
      } else {
        px(40)
      }
    }

    for ((appType, appTile) in appTileImageViews.entries) {
      appTile.image = controller.getImage(appType)
    }

    val leftProduct = controller.getProduct(
      productPeriod = controller.leftToggleProductPeriod,
      currencyCode = currencyCode
    )
    val rightProduct = controller.getProduct(
      productPeriod = controller.rightToggleProductPeriod,
      currencyCode = currencyCode
    )

    listOf(
      leftToggle to leftProduct,
      rightToggle to rightProduct
    ).forEach { (toggle, product) ->
      toggle.setHeading(
        when (product.productPeriod) {
          MONTHLY -> Strings.MONTHLY_CAPITALIZED
          YEARLY -> Strings.YEARLY_CAPITALIZED
        }
      )
      toggle.setSubheading(
        when (product.productPeriod) {
          MONTHLY -> Strings.PER_MONTH
          YEARLY -> Strings.PER_YEAR
        }
      )
      toggle.setPrice(product.price!!)
      toggle.setAppPrice(product.appPrice)
      toggle.setYearlyPricePerMonth(product.yearlyPricePerMonth)
    }
  }

  private fun HTMLDivElement.productToggle(left: Boolean): ProductToggle {
    val toggle = ProductToggle(
      clickFn = {
        productPeriod = if (left) YEARLY else MONTHLY
        leftToggle.setBackgroundSelected(
          selected = left
        )
        rightToggle.setBackgroundSelected(
          selected = !left
        )
        refreshView()
      }
    )
    this.appendChild(toggle.root)
    return toggle
  }

  private class ProductToggle(clickFn: ProductToggle.() -> Unit = {}) : AbstractView() {

    private var heading: HTMLSpanElement
    private var subheading: HTMLSpanElement
    private var appStorePrice: HTMLSpanElement
    private var price: HTMLSpanElement
    private var yearlyPricePerMonth: HTMLSpanElement

    override val root = createDiv {
      screenWidthDependent { screenWidth ->
        width = px(if (screenWidth == PHONE) 135 else 217)
        height = px(if (screenWidth == PHONE) 175 else 220)
      }
      bgColor = Color.PRODUCT_TOGGLE_BACKGROUND
      borderRadius = px(8)
      marginHorizontal = px(5)

      centerHorizontally {
        height = percent(100)

        heading = label(
          text = null,
          fontWeight = SEMIBOLD,
          fontSize = 22
        ) {
          screenWidthDependent { screenWidth ->
            marginTop = px(if (screenWidth == PHONE) 30 else 39)
            fontSize = px(if (screenWidth == PHONE) 16 else 22)
          }
        }
        appStorePrice = label(
          text = null,
          fontSize = 20, // this is overridden in setAppPrice
          fontWeight = REGULAR
        ) {
          marginTop = px(15)
          style.textDecoration = "line-through"
          opacity = 0.8
          screenWidthDependent { screenWidth ->
            fontSize = px(if (screenWidth == PHONE) 13 else 20)
          }
        }
        price = label(
          text = null,
          fontSize = 30,
          fontWeight = SEMIBOLD
        ) {
          marginTop = px(4)
          screenWidthDependent { screenWidth ->
            fontSize = px(if (screenWidth == PHONE) 20 else 30)
          }
        }
        subheading = label(
          text = null,
          fontWeight = SEMIBOLD,
          fontSize = 15
        ) {
          marginTop = px(4)
          opacity = 0.8
          screenWidthDependent { screenWidth ->
            fontSize = px(if (screenWidth == PHONE) 13 else 15)
          }
        }
        yearlyPricePerMonth = label(
          text = null,
          fontSize = 15
        ) {
          marginTop = px(12)
          opacity = 0.8
          screenWidthDependent { screenWidth ->
            fontSize = px(if (screenWidth == PHONE) 13 else 15)
          }
        }
      }

      onClick {
        clickFn()
        blur()
      }
    }

    fun setBackgroundSelected(selected: Boolean) {
      if (selected) {
        root.boxShadow = BoxShadow.boxShadow(px(0), px(0), px(0), px(4), true, Color.DOWN_DOG_BLUE)
      } else {
        root.boxShadow = BoxShadow.NONE
      }
    }

    fun setHeading(text: String) {
      heading.text = text
    }

    fun setSubheading(text: String?) {
      subheading.text = text
    }

    fun setAppPrice(priceStr: String?) {
      appStorePrice.text = priceStr ?: ""
    }

    fun setPrice(priceStr: String) {
      price.text = priceStr
    }

    fun setYearlyPricePerMonth(priceStr: String?) {
      yearlyPricePerMonth.text = priceStr ?: ""
    }
  }

  companion object {

    var productPeriod = MANIFEST.membershipConfig.defaultProductPeriod; private set
    var currencyCode = MANIFEST.membershipConfig.defaultCurrencyCode; private set
  }
}

// These definitions need to go here instead of elsewhere, otherwise the compiler complains
@ExperimentalJsExport
@JsExport
fun createPayPalSubscription(@Suppress("UNUSED_PARAMETER") data: dynamic, actions: dynamic): Promise<String> {
  return PayPal
    .createSubscription(
      productPeriod = PurchaseView.productPeriod,
      currencyCode = PurchaseView.currencyCode
    )
    .then {
      actions.subscription.create(
        jsObj(
          "plan_id" to it
        )
      )
    }
}

@ExperimentalJsExport
@JsExport
@Suppress("UNUSED_PARAMETER")
fun createPayPalMembership(data: dynamic, actions: dynamic) {
  PayPal.createMembership(
    productPeriod = PurchaseView.productPeriod,
    currencyCode = PurchaseView.currencyCode,
    subscriptionId = data.subscriptionID
  )
}
